import { createLazyFileRoute } from "@tanstack/react-router";

import { trpc } from "@blis/trpc/react";

import { ErrorPage } from "@/components/ErrorPage";
import { LoadingPage } from "@/components/LoadingPage";
import { Breadcrumb } from "@/components/ui/breadcrumbs";
import { ProcessVersionEditor } from "@/features/admin/components/processes/ProcessVersionEditor";
import { Section } from "@/features/admin/components/Section";

export const Route = createLazyFileRoute(
  "/_auth/admin/processes/$id/versions/$versionId",
)({
  component: RouteComponent,
});

function RouteComponent() {
  const params = Route.useParams();
  const isCreateMode = params.versionId === "new";

  const {
    data: template,
    isError: templateError,
    isPending: templatePending,
    error: templateQueryError,
  } = trpc.admin.process.getTemplate.useQuery({ id: params.id });

  // Only query for version if not in create mode
  const {
    data: version,
    isError: versionError,
    isPending: versionPending,
    error: versionQueryError,
  } = trpc.admin.process.getVersion.useQuery(
    { id: Number.parseInt(params.versionId, 10) },
    { enabled: !isCreateMode },
  );

  if (templatePending || (!isCreateMode && versionPending)) {
    return <LoadingPage />;
  }

  if (!template) {
    return <ErrorPage error={new Error("Process template not found")} />;
  }

  if (!isCreateMode && !version) {
    return <ErrorPage error={new Error("Process version not found")} />;
  }

  if (templateError) {
    return <ErrorPage error={templateQueryError} />;
  }

  if (!isCreateMode && versionError) {
    return <ErrorPage error={versionQueryError} />;
  }

  // Get next version number for create mode
  const getNextVersionNumber = () => {
    if (template.versions.length === 0) return 1;
    const latestVersion = Math.max(...template.versions.map((v) => v.version));
    return latestVersion + 1;
  };

  const breadcrumbTitle = isCreateMode
    ? `Version ${getNextVersionNumber()} (New)`
    : `Version ${version?.version}`;

  const sectionTitle = isCreateMode
    ? `Create Version ${getNextVersionNumber()}`
    : `Version ${version?.version}`;

  return (
    <>
      <Section.Title
        breadcrumb={
          <>
            <Breadcrumb href={{ to: "/admin/processes" }}>Processes</Breadcrumb>
            <Breadcrumb
              href={{ to: "/admin/processes/$id", params: { id: template.id } }}
            >
              {template.name}
            </Breadcrumb>
            <Breadcrumb>{breadcrumbTitle}</Breadcrumb>
          </>
        }
      >
        {sectionTitle}
      </Section.Title>
      <ProcessVersionEditor template={template} versionId={params.versionId} />
    </>
  );
}
